/**
 * Dictation control for one speech-input controller. It renders recording
 * state and microphone levels while its caller owns capture and transcription.
 */
import { RecordingStatus } from '@littlebird/sdk';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack } from '@/components/ui';
import { type TooltipProps } from '@/components/ui/Tooltip';
import { RoundIconButton } from '@/design-system-v2/round-icon-button';
import { useAppShortcut } from '@/features/shortcuts';
import { MicrophoneInputDeviceMenu } from '@/features/speech-input/microphone-input-device-menu';
import { EASE } from '@/lib/animate';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { createAudioContext } from '@/utils/audio-context';

import {
  createBarMeter,
  IDLE_BARS,
  type BarLayout,
} from './audio-visualizer-bars';

interface MicrophoneButtonProps {
  mediaStreamRef: { readonly current: MediaStream | null };
  recordingStatus: RecordingStatus;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onCancelRecording: () => void;
  disabled?: boolean;
  tooltipSide?: TooltipProps['side'];
  enableHotkeys?: boolean;
  disableLayoutAnimation?: boolean;
  className?: string;
}

export function MicrophoneButton({
  mediaStreamRef,
  recordingStatus,
  onStartRecording: startRecording,
  onStopRecording,
  onCancelRecording,
  disabled = false,
  tooltipSide,
  enableHotkeys = true,
  disableLayoutAnimation = false,
  className,
}: MicrophoneButtonProps) {
  const shouldReduceMotion = useReducedMotion();
  const isRecording = recordingStatus === RecordingStatus.Recording;
  const isProcessing = recordingStatus === RecordingStatus.Processing;

  const finishRecording = () => {
    onStopRecording();
    trackEvent('MESSAGE_USER_VOICE');
  };

  useAppShortcut(
    'chat.voiceInput',
    () => {
      if (isRecording) finishRecording();
      else if (!isProcessing && !disabled) startRecording();
    },
    {
      enabled: enableHotkeys,
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
    },
    [disabled, isProcessing, isRecording, startRecording, finishRecording]
  );

  useAppShortcut(
    'chat.cancelVoiceInput',
    () => {
      if (isRecording) onCancelRecording();
    },
    {
      enabled: enableHotkeys,
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
    },
    [isRecording, onCancelRecording]
  );

  const tooltip = isRecording
    ? 'Finish voice input'
    : isProcessing
      ? 'Processing voice input'
      : 'Start voice input · Right-click to choose microphone';
  const tooltipHotkey =
    enableHotkeys && !isProcessing ? 'chat.voiceInput' : undefined;

  return (
    <motion.div
      layout={!disableLayoutAnimation && !shouldReduceMotion}
      layoutDependency={recordingStatus}
      className="flex items-center justify-end overflow-hidden cursor-default"
      transition={{ type: 'tween', duration: 0.18, ease: EASE.outQuint }}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {isRecording ? (
          <motion.div
            key="recording-view"
            initial={{ opacity: 0, filter: 'blur(2px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, filter: 'blur(2px)' }}
          >
            <XStack className="items-center gap-1.5">
              <RoundIconButton
                size="sm"
                className="focus-ring-inset transform-gpu"
                icon={Icons.cancel}
                onClick={onCancelRecording}
                label="Cancel voice input"
                aria-label="Cancel recording"
                hotkey={enableHotkeys ? 'chat.cancelVoiceInput' : undefined}
                tooltipSide={tooltipSide}
              />
              <AudioDurationCounter />
              <AudioVisualizer mediaStream={mediaStreamRef.current} />
              <RoundIconButton
                size="sm"
                className="focus-ring-inset transform-gpu"
                icon={Icons.check}
                onClick={finishRecording}
                label={tooltip}
                aria-label="Finish recording"
                hotkey={tooltipHotkey}
                tooltipSide={tooltipSide}
              />
            </XStack>
          </motion.div>
        ) : (
          <motion.span
            key="icon-record-audio"
            initial={{ opacity: 0, filter: 'blur(2px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, filter: 'blur(2px)' }}
          >
            <MicrophoneInputDeviceMenu>
              <span className="inline-flex">
                <RoundIconButton
                  size="sm"
                  className={cn(
                    'focus-ring-inset transform-gpu',
                    isProcessing && '[&_svg]:animate-spin',
                    className
                  )}
                  icon={isProcessing ? Icons.loader : Icons.recordAudio}
                  onClick={startRecording}
                  disabled={disabled || isProcessing}
                  label={tooltip}
                  hotkey={tooltipHotkey}
                  tooltipSide={tooltipSide}
                  aria-label={
                    isProcessing
                      ? 'Processing voice input'
                      : 'Start voice recording'
                  }
                  aria-busy={isProcessing}
                />
              </span>
            </MicrophoneInputDeviceMenu>
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/**
 * Delay before starting the displayed elapsed-time clock. This counter mounts
 * only after the browser recorder reports that recording has started.
 */
const RECORDING_START_DELAY_MS = 100;
const AudioDurationCounter = () => {
  const [seconds, setSeconds] = useState(0);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    let interval: ReturnType<typeof setTimeout>;

    const delayTimer = setTimeout(() => {
      const startTime = Date.now();
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setSeconds(elapsed);
      }, 1000);
    }, RECORDING_START_DELAY_MS);

    return () => {
      clearTimeout(delayTimer);
      if (interval) clearInterval(interval);
    };
  }, []);

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  const timeString = `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  return (
    <>
      <span
        className="sr-only"
        role="timer"
        aria-live="polite"
        aria-atomic="true"
      >
        Recording time: {timeString}
      </span>

      <XStack className="items-center cursor-default" aria-hidden="true">
        <AnimatePresence initial={false} mode="popLayout">
          {timeString.split('').map((char, i) => (
            <motion.span
              className="inline-block tabular-nums text-xs text-foreground-muted"
              key={`${char}-${i}`}
              initial={{
                y: shouldReduceMotion ? '0' : '8px',
                filter: 'blur(2px)',
                opacity: 0,
              }}
              animate={{ y: '0', filter: 'blur(0px)', opacity: 1 }}
              exit={{
                y: shouldReduceMotion ? '0' : '-8px',
                filter: 'blur(2px)',
                opacity: 0,
              }}
              transition={{
                type: 'spring',
                bounce: 0.25,
                duration: 0.3,
              }}
            >
              {char}
            </motion.span>
          ))}
        </AnimatePresence>
      </XStack>
    </>
  );
};

export const AudioVisualizer = ({
  mediaStream,
  barLayout,
}: {
  mediaStream: MediaStream | null;
  barLayout?: BarLayout;
}) => {
  const [bars, setBars] = useState<number[]>(IDLE_BARS);

  useEffect(() => {
    if (!mediaStream) {
      setBars(IDLE_BARS);
      return;
    }

    const audioContext = createAudioContext();
    if (!audioContext) {
      console.warn('Web Audio API not supported');
      return;
    }

    try {
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      // Favor visible responsiveness over a slowly averaged level
      analyser.smoothingTimeConstant = 0.3;

      const source = audioContext.createMediaStreamSource(mediaStream);
      const gainNode = audioContext.createGain();
      // Amplify measurements only; this node is not connected to the speaker
      gainNode.gain.value = 4;

      source.connect(gainNode);
      gainNode.connect(analyser);

      const frequencyDb = new Float32Array(analyser.frequencyBinCount);
      const nextBars = createBarMeter(
        audioContext.sampleRate,
        analyser.fftSize,
        barLayout
      );
      let animationFrameId: number;
      const updateBars = () => {
        analyser.getFloatFrequencyData(frequencyDb);
        setBars(nextBars(frequencyDb));
        animationFrameId = requestAnimationFrame(updateBars);
      };

      updateBars();

      return () => {
        source.disconnect();
        gainNode.disconnect();
        audioContext.close();

        cancelAnimationFrame(animationFrameId);
      };
    } catch (error) {
      void audioContext.close();
      console.error('Error setting up audio visualizer:', error);
    }
  }, [mediaStream, barLayout]);

  return (
    <XStack className="items-center gap-1" data-testid="audio-visualizer">
      {bars.map((height, i) => (
        <motion.div
          key={i}
          className="w-[2px] shrink-0 bg-foreground-subtle rounded-full cursor-default"
          animate={{
            height: Math.max(2, Math.min(16, height)),
          }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25,
            mass: 0.3,
          }}
        />
      ))}
    </XStack>
  );
};
